import React from 'react';
import { ArrowLeft, X, Zap, Info } from 'lucide-react';
import { useApp } from './AppContext';
import { useTranslation } from '../i18n/TranslationContext';

export function IDCapture() {
  const { setCurrentScreen, navigateToProfileCompletion, completeIDCheck, idCheckState, useReplacement, navigationContext } = useApp();
  const { t } = useTranslation();

  const handleBack = () => {
    // Context-sensitive back navigation
    if (idCheckState.navigationContext === 'replacement') {
      setCurrentScreen('id-result');
    } else {
      setCurrentScreen('id-terms');
    }
  };

  const handleClose = () => {
    // Navigate back to source screen
    if (navigationContext?.sourceScreen) {
      setCurrentScreen(navigationContext.sourceScreen);
    } else {
      setCurrentScreen('app-profile-completion-overview');
    }
  };

  const handleCapture = () => {
    // Handle both initial and replacement flows
    if (idCheckState.navigationContext === 'replacement') {
      // This is a replacement - decrement counter
      useReplacement();
      // Update the upload date
      completeIDCheck();
    } else {
      // This is initial completion
      completeIDCheck();
    }
    setCurrentScreen('id-result');
  };

  return (
    <div className="fixed inset-0 bg-[#1f1f1f] z-50">
      {/* Status Bar */}
      <div className="flex items-center justify-between px-6 pt-3 pb-2 text-white text-sm">
        <span className="font-medium">9:41</span>
        <div className="flex items-center space-x-1">
          <div className="flex space-x-1">
            <div className="w-1 h-3 bg-white rounded-full"></div>
            <div className="w-1 h-3 bg-white rounded-full"></div>
            <div className="w-1 h-3 bg-white rounded-full"></div>
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
          <svg className="w-6 h-3 ml-2" viewBox="0 0 24 12" fill="none">
            <rect x="1" y="2" width="18" height="8" rx="2" stroke="white" strokeWidth="1" fill="none"/>
            <rect x="20" y="4" width="2" height="4" rx="1" fill="white"/>
            <rect x="3" y="4" width="14" height="4" rx="1" fill="white"/>
          </svg>
        </div>
      </div>

      {/* Header Navigation */}
      <div className="flex items-center justify-between px-6 py-4">
        <button
          onClick={handleBack}
          className="w-10 h-10 flex items-center justify-center"
        >
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>
        
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Camera Viewfinder */}
      <div className="flex-1 flex items-center justify-center px-8">
        <div className="relative w-full max-w-sm">
          {/* Capture Frame */}
          <div className="relative aspect-[4/3] border-2 border-white rounded-2xl overflow-hidden">
            {/* Corner Brackets */}
            <div className="absolute top-2 left-2">
              <div className="w-6 h-6 border-l-3 border-t-3 border-white rounded-tl-lg"></div>
            </div>
            <div className="absolute top-2 right-2">
              <div className="w-6 h-6 border-r-3 border-t-3 border-white rounded-tr-lg"></div>
            </div>
            <div className="absolute bottom-2 left-2">
              <div className="w-6 h-6 border-l-3 border-b-3 border-white rounded-bl-lg"></div>
            </div>
            <div className="absolute bottom-2 right-2">
              <div className="w-6 h-6 border-r-3 border-b-3 border-white rounded-br-lg"></div>
            </div>

            {/* ID Card Illustration */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-32 h-20 border-2 border-white/60 rounded-lg flex items-center justify-center">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-10 border border-white/60 rounded flex items-center justify-center">
                    <div className="w-3 h-3 bg-white/60 rounded-full"></div>
                  </div>
                  <div className="space-y-1">
                    <div className="w-12 h-1 bg-white/60 rounded"></div>
                    <div className="w-10 h-1 bg-white/60 rounded"></div>
                    <div className="w-14 h-1 bg-white/60 rounded"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ID Card Icon and Instructions */}
          <div className="mt-6 text-center">
            <div className="inline-flex items-center justify-center w-12 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-md mb-3">
              <div className="w-8 h-5 bg-white/20 rounded-sm flex items-center justify-center">
                <div className="w-1 h-1 bg-white rounded-full"></div>
              </div>
            </div>
            
            <p className="text-white text-lg font-medium mb-2">
              {idCheckState.navigationContext === 'replacement' 
                ? t('idCapture.placeNewIdInFrame') 
                : t('idCapture.placeIdInFrame')
              }
            </p>
            
            {idCheckState.navigationContext === 'replacement' && (
              <p className="text-white/70 text-sm mb-2">
                {t('idCapture.replacingIdPhoto')}
              </p>
            )}
            
            <div className="flex items-center justify-center space-x-1 text-white/80">
              <Info className="w-4 h-4" />
              <span className="text-sm">{t('idCapture.captureTips')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Controls */}
      <div className="pb-8 px-8">
        <div className="flex items-center justify-center space-x-8">
          <div className="w-12"></div>
          
          <button
            onClick={handleCapture}
            className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-105 transition-transform duration-200"
          >
            <div className="w-16 h-16 bg-white rounded-full border-4 border-gray-300"></div>
          </button>
          
          <button className="w-12 h-12 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Bottom Indicator */}
        <div className="flex justify-center mt-4">
          <div className="w-12 h-1 bg-white rounded-full"></div>
        </div>
      </div>

      {/* Footer Branding */}
      <div className="absolute bottom-4 left-0 right-0 px-6">
        <div className="flex items-center justify-between text-white/60 text-xs">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-white/60 rounded"></div>
            <span>Kraken</span>
          </div>
          <span>curated by Mobbin</span>
        </div>
      </div>
    </div>
  );
}